import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, Calendar, User, Mail, Loader } from 'lucide-react';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hi! 👋 I\'m CALLOC AI Assistant. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackData, setCallbackData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (type, text) => {
    setMessages(prev => [...prev, { type, text, timestamp: new Date() }]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue;
    setInputValue('');
    addMessage('user', userMessage);
    setIsLoading(true);

    // Check for callback/schedule keywords
    const callbackKeywords = ['call back', 'callback', 'schedule', 'appointment', 'call me', 'contact me', 'reach out'];
    const isCallbackRequest = callbackKeywords.some(keyword => userMessage.toLowerCase().includes(keyword));

    if (isCallbackRequest) {
      setIsLoading(false);
      addMessage('bot', 'I\'d be happy to help you schedule a callback! Please fill in the form below and our team will reach out to you soon.');
      setShowCallbackForm(true);
      return;
    }

    try {
      // Call Gemini API
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      console.log('API Key present:', !!apiKey);
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `You are an AI assistant for CALLOC Technologies Private Limited, a software development company in Karnataka, India. 
              
              Company Info:
              - Services: Web Development, Mobile Apps, AI/ML, Cloud Solutions, Industrial Training, Campus2Training (technical talks & bootcamps), IT Consulting
              - Location: Mysore, Karnataka
              - Contact: officecalloc@gmail.com, +91 8971504091
              - Notable Projects: Toppers Academy (1000+ students), Brainbeans Portal (Australia), GK Academy (5000+ downloads)
              - Expertise: React.js, Node.js, React Native, Python, AI/ML, Firebase, AWS
              
              Answer the following question helpfully and professionally in 2-3 sentences. If asked about scheduling calls, appointments, or callbacks, suggest they can request a callback.
              
              Question: ${userMessage}`
            }]
          }]
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`API Error: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      const botResponse = data.candidates?.[0]?.content?.parts?.[0]?.text || 
        "I'm here to help! You can ask me about our services, projects, or request a callback from our team.";
      
      addMessage('bot', botResponse);
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      addMessage('bot', 'I apologize, but I\'m having trouble connecting right now. Please email us at officecalloc@gmail.com or call +91 8971504091 for immediate assistance.');
    }

    setIsLoading(false);
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Send callback request via email (using a backend API or EmailJS)
      // For now, we'll simulate success
      const response = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...callbackData,
          subject: 'Callback Request from Website Chatbot',
          _replyto: callbackData.email
        })
      }).catch(() => {
        // Fallback: Open email client
        const mailtoLink = `mailto:officecalloc@gmail.com?subject=Callback Request&body=Name: ${callbackData.name}%0D%0AEmail: ${callbackData.email}%0D%0APhone: ${callbackData.phone}%0D%0APreferred Date: ${callbackData.preferredDate}%0D%0APreferred Time: ${callbackData.preferredTime}%0D%0AMessage: ${callbackData.message}`;
        window.location.href = mailtoLink;
      });

      addMessage('bot', `Thank you, ${callbackData.name}! 🎉 We've received your callback request. Our team will contact you on ${callbackData.preferredDate} at ${callbackData.preferredTime}. You'll receive a confirmation email at ${callbackData.email} shortly.`);
      setShowCallbackForm(false);
      setCallbackData({ name: '', email: '', phone: '', preferredDate: '', preferredTime: '', message: '' });
    } catch (error) {
      addMessage('bot', 'There was an issue submitting your request. Please email us directly at officecalloc@gmail.com or call +91 8971504091.');
    }

    setIsLoading(false);
  };

  const quickActions = [
    { text: 'Our Services', icon: '🎯' },
    { text: 'Campus2Training', icon: '🎓' },
    { text: 'Request Callback', icon: '📞' },
    { text: 'View Projects', icon: '💼' }
  ];

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
            AI
          </span>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-24 right-6 z-50 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold">CALLOC AI Assistant</h3>
                    <p className="text-xs text-blue-100">Powered by Gemini AI</p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white'
                        : 'bg-white shadow-md text-gray-800'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                    <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </motion.div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white shadow-md p-3 rounded-2xl">
                    <Loader className="w-5 h-5 animate-spin text-blue-600" />
                  </div>
                </div>
              )}

              {/* Callback Form */}
              {showCallbackForm && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white p-4 rounded-xl shadow-lg"
                >
                  <h4 className="font-bold text-gray-800 mb-3">Schedule a Callback</h4>
                  <form onSubmit={handleCallbackSubmit} className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-600 flex items-center space-x-1 mb-1">
                        <User className="w-3 h-3" />
                        <span>Name *</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={callbackData.name}
                        onChange={(e) => setCallbackData({ ...callbackData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 flex items-center space-x-1 mb-1">
                        <Mail className="w-3 h-3" />
                        <span>Email *</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={callbackData.email}
                        onChange={(e) => setCallbackData({ ...callbackData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 flex items-center space-x-1 mb-1">
                        <Phone className="w-3 h-3" />
                        <span>Phone *</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={callbackData.phone}
                        onChange={(e) => setCallbackData({ ...callbackData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="+91 XXXXXXXXXX"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600 flex items-center space-x-1 mb-1">
                          <Calendar className="w-3 h-3" />
                          <span>Date *</span>
                        </label>
                        <input
                          type="date"
                          required
                          value={callbackData.preferredDate}
                          onChange={(e) => setCallbackData({ ...callbackData, preferredDate: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Time *</label>
                        <input
                          type="time"
                          required
                          value={callbackData.preferredTime}
                          onChange={(e) => setCallbackData({ ...callbackData, preferredTime: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Message (Optional)</label>
                      <textarea
                        value={callbackData.message}
                        onChange={(e) => setCallbackData({ ...callbackData, message: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        placeholder="Any specific requirements?"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                      >
                        {isLoading ? 'Submitting...' : 'Submit Request'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCallbackForm(false)}
                        className="px-4 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {!showCallbackForm && (
              <div className="px-4 py-2 bg-white border-t flex flex-wrap gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setInputValue(action.text);
                      handleSendMessage();
                    }}
                    className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors"
                  >
                    {action.icon} {action.text}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-4 bg-white border-t">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                  className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-2 rounded-full hover:shadow-lg transition-all disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
